import { useRef, useState, useEffect } from 'react';
import QRCodeStyling from '@plasticprintersinc/pp-qr-code';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import Dropdown from 'react-bootstrap/Dropdown';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useTypeHeaderIcon } from '../../../../utils/hooks/useTypeHeaderIcon';
import { useAuth } from '../../../../utils/Auth';
import { motion } from 'motion/react';
import { transition, variants } from './variants';
import { useWindowSize } from '../../../../utils/hooks/useWindowSize';
import styles from './List.module.css';

export default function List(props) {

    const { QR, total, childQRs } = props;

    const { userObj, orgObj } = useAuth();
    const qrRef = useRef(null);
    const navigate = useNavigate();
    const { width } = useWindowSize();
    const [ showInfo, setShowInfo ] = useState(false);
    
    const { ref, inView } = useInView({
        root: null,
        rootMargin: '0px 0px -25px',
        threshold: .25
    });

    const { icon, header } = useTypeHeaderIcon(QR?.data?.qr_type);

    const options = {...QR?.data?.qr_style, width: 83, height: 83};
    const isBlurred = userObj?.plan?.plan_id === 0 && QR?.id.includes('-');
    const nickname = QR?.data?.nickname || QR?.nickname;
    const [ code, setCode ] = useState(inView ? new QRCodeStyling(options) : null);

    useEffect(() => {
        if (inView && !code && options) {
            setCode(() => new QRCodeStyling(options));
        } else if (inView && code && options) {
            code.update(options);
            code.append(qrRef.current);
        };
    },[inView, code, options, setCode]);
    // end onload and update

    const handleViewDetails = (tab) => {
        navigate(`/collection/${QR.id}/details?tab=${tab}`);
    };

    return (
        <div id={QR?.id} ref={ref} className={styles.container} onClick={() => handleViewDetails('details')}>
            <div className={styles.codeWrap}>
                <div className={`${styles.code} ${isBlurred ? styles.blur : ''}`} ref={qrRef}>
                    {!code && <i className='fa-sharp fa-regular fa-qrcode' />}
                </div>
                {isBlurred && ( 
                    <div className={styles.band}>Sticker</div>
                )}
            </div>
            <div className={styles.textWrap}>
                <OverlayTrigger
                    placement='top'
                    delay={{show: 250, hide: 400}}
                    trigger={['click','hover']}
                    overlay={<Tooltip>{nickname}</Tooltip>}
                >
                    <div className={styles.nickname} title={nickname} onClick={e => e.stopPropagation()}><span>{nickname}</span></div>
                </OverlayTrigger>
                <div className={styles.qrType}>Type: <span>{header}</span><i className={icon} /></div>
                <div className={styles.qrId}>ID: <span>{QR?.id}</span></div>
            </div>
            <div className={styles.filler} style={{minWidth: showInfo ? '6rem' : '2rem'}}/>
            <motion.div 
                className={styles.infoWrap}
                transition={transition}
                variants={variants}
                initial={false}
                animate={width <= 500 ? showInfo ? 'animate' : 'initial' : 'animate'}
                custom={width}
            >
                {width <= 500 && (<button className={styles.infoBtn} onClick={(e) => {e.stopPropagation(); setShowInfo(!showInfo);}}><i className={`${showInfo ? 'far fa-arrow-right' : 'far fa-arrow-left'}`} /></button>)}
                <motion.div 
                    className={styles.infoWrapInner}
                    transition={transition}
                    variants={variants}
                    initial={false}
                    animate={width <= 500 ? showInfo ? 'animate2' : 'initial2' : 'animate2'}
                >
                    <OverlayTrigger
                        placement='top'
                        delay={{show: 250, hide: 400}}
                        trigger={['click','hover']}
                        overlay={<Tooltip>Total Scans include linked QR scans.</Tooltip>}
                    >
                        <div className={styles.scanCount} onClick={e => e.stopPropagation()}><i className='fas fa-eye me-1' />{total}</div>
                    </OverlayTrigger>
                    <OverlayTrigger
                        placement='top'
                        delay={{show: 250, hide: 400}}
                        trigger={['click','hover']}
                        overlay={<Tooltip>View Linked QRs</Tooltip>}
                    >
                        <button className={styles.childCount} onClick={(e) => {e.stopPropagation(); handleViewDetails('linked')}} disabled={childQRs?.length === 0}><i className='fa-regular fa-object-exclude me-1' />{childQRs?.length}</button>
                    </OverlayTrigger>
                </motion.div>
            </motion.div>
        </div>
    );
};